import type { CSSProperties, FC, ReactNode } from 'react';
import { Col, Divider, Row, Space, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';

interface SkeletonLineProps {
    height?: CSSProperties['height'];
    style?: CSSProperties;
    width: number;
}

const SkeletonLine: FC<SkeletonLineProps> = ({ height = 10, style, width }): JSX.Element => (
    <ButtonSkeleton
        active
        shape='default'
        size='small'
        style={{ height, maxWidth: '100%', minWidth: 0, width, ...style }}
    />
);

interface SkeletonFieldProps {
    span?: 12 | 24;
}

const SkeletonField: FC<SkeletonFieldProps> = ({ span = 12 }): JSX.Element => (
    <Col xs={24} md={span}>
        <SkeletonLine style={{ display: 'block', marginBottom: '0.375rem' }} width={96} />
        <ButtonSkeleton active block shape='default' style={{ height: 32 }} />
    </Col>
);

const SkeletonToggleRow: FC = (): JSX.Element => (
    <Col span={24}>
        <Row align='middle' gutter={[24, 8]} wrap={false}>
            <Col flex='auto'>
                <SkeletonLine style={{ display: 'block', marginBottom: '0.375rem' }} width={180} />
                <SkeletonLine height={8} style={{ display: 'block' }} width={420} />
            </Col>
            <Col flex='none'>
                <ButtonSkeleton active size='small' style={{ height: 16, minWidth: 28, width: 28 }} />
            </Col>
        </Row>
    </Col>
);

interface SkeletonCardProps {
    children: ReactNode;
    style?: CSSProperties;
}

const SkeletonCard: FC<SkeletonCardProps> = ({ children, style }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <section
            style={{
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                padding: '1rem',
                ...style,
            }}
        >
            <Row align='middle' gutter={[6, 0]} wrap={false}>
                <Col flex='none'>
                    <AvatarSkeleton active shape='square' size={20} />
                </Col>
                <Col flex='auto'>
                    <ButtonSkeleton active shape='default' size='small' style={{ height: 20, width: 168 }} />
                </Col>
            </Row>
            <SkeletonLine height={8} style={{ display: 'block', margin: '0.75rem 0 1rem' }} width={520} />
            {children}
        </section>
    );
};

const PaletteSkeleton: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row gutter={[12, 12]}>
            {Array.from({ length: 4 }).map((_, index) => (
                <Col key={index} xs={24} md={12}>
                    <div
                        style={{
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            padding: '0.75rem',
                        }}
                    >
                        <Row align='middle' gutter={[8, 0]} wrap={false}>
                            <Col flex='none'>
                                <AvatarSkeleton active shape='square' size={28} />
                            </Col>
                            <Col flex='auto'>
                                <SkeletonLine width={index % 2 === 0 ? 140 : 112} />
                            </Col>
                        </Row>
                        <SkeletonLine
                            height={8}
                            style={{ display: 'block', marginTop: '0.75rem' }}
                            width={index % 2 === 0 ? 280 : 240}
                        />
                    </div>
                </Col>
            ))}
        </Row>
    );
};

const SectionHeadingSkeleton: FC<{ description: string; title: string }> = ({ description, title }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <>
            <Typography.Title level={4} style={{ marginBottom: '0.25rem' }}>
                {title}
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: '1rem' }}>
                {description}
            </Typography.Paragraph>
        </>
    );
};

const WhitelabelingSkeleton: FC = (): JSX.Element => (
    <div aria-busy='true' aria-label='Loading appearance settings' role='status' style={{ marginTop: '1rem' }}>
        <section>
            <SectionHeadingSkeleton
                description='Configure the theme members use throughout this workspace.'
                title='Workspace'
            />
            <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                <SkeletonCard>
                    <Row gutter={[12, 12]}>
                        <SkeletonField span={24} />
                    </Row>
                </SkeletonCard>
                <SkeletonCard>
                    <PaletteSkeleton />
                </SkeletonCard>
                <SkeletonCard>
                    <Row gutter={[12, 12]}>
                        <SkeletonField span={24} />
                        <SkeletonToggleRow />
                    </Row>
                </SkeletonCard>
                <SkeletonCard>
                    <Row gutter={[12, 12]}>
                        {Array.from({ length: 6 }).map((_, index) => (
                            <SkeletonField key={index} />
                        ))}
                    </Row>
                </SkeletonCard>
            </Space>
        </section>

        <Divider />

        <section>
            <SectionHeadingSkeleton
                description='Configure the branded header, content, and footer shown on public resources.'
                title='Shared Resources'
            />
            <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                <SkeletonCard>
                    <Row gutter={[12, 12]}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <SkeletonField key={index} />
                        ))}
                        <SkeletonToggleRow />
                        <SkeletonToggleRow />
                    </Row>
                </SkeletonCard>
                <SkeletonCard>
                    <Row gutter={[12, 12]}>
                        {Array.from({ length: 6 }).map((_, index) => (
                            <SkeletonField key={index} />
                        ))}
                    </Row>
                </SkeletonCard>
                <SkeletonCard>
                    <Row gutter={[12, 12]}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <SkeletonField key={index} />
                        ))}
                        <SkeletonToggleRow />
                    </Row>
                </SkeletonCard>
            </Space>
        </section>

        <Row gutter={[16, 0]} style={{ marginTop: '1.5rem' }}>
            <Col span={12}>
                <ButtonSkeleton active block shape='default' />
            </Col>
            <Col span={12}>
                <ButtonSkeleton active block shape='default' />
            </Col>
        </Row>
    </div>
);

export default WhitelabelingSkeleton;
